import { type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ListFilterBarSegment {
  id: string;
  label: string;
  count: number;
}

export interface ListFilterBarProps {
  filter: string;
  onFilterChange: (value: string) => void;
  placeholder: string;
  mono?: boolean;
  segments?: ListFilterBarSegment[];
  activeSegment?: string;
  onSegmentChange?: (id: string) => void;
  extra?: ReactNode;
  className?: string;
}

export function ListFilterBar({
  filter,
  onFilterChange,
  placeholder,
  mono,
  segments,
  activeSegment,
  onSegmentChange,
  extra,
  className,
}: ListFilterBarProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 flex-wrap flex-shrink-0',
        'px-[14px] py-2 bg-surface border-b border-borderSoft',
        className
      )}
    >
      <div className="relative flex-1 min-w-[200px] basis-[220px]">
        <span className="absolute left-[9px] top-1/2 -translate-y-1/2 flex text-textSoft">
          <Search size={12} />
        </span>
        <input
          value={filter}
          onChange={e => onFilterChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'w-full h-7 pl-7 pr-[9px] rounded-md outline-none',
            'bg-bg border border-border text-text text-[11.5px]',
            mono ? 'font-mono' : 'font-sans'
          )}
        />
      </div>
      {segments && (
        <div className="inline-flex bg-surfaceAlt p-0.5 rounded-md border border-border">
          {segments.map(s => {
            const active = s.id === activeSegment;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSegmentChange?.(s.id)}
                className={cn(
                  'h-[22px] px-[9px] rounded',
                  'inline-flex items-center gap-1',
                  'font-mono text-[10.5px] font-bold tracking-[.02em] cursor-pointer',
                  active
                    ? 'bg-surface text-text shadow-[0_1px_2px_rgba(0,0,0,.05)]'
                    : 'bg-transparent text-textMute'
                )}
              >
                {s.label}{' '}
                <span className="opacity-60 text-[9.5px]">{s.count}</span>
              </button>
            );
          })}
        </div>
      )}
      {extra}
    </div>
  );
}

ListFilterBar.displayName = 'ListFilterBar';

export default ListFilterBar;
